import ActionTypes from '../actions/types'
import { createReducer } from 'reduxsauce'
import { GlobalConfig } from 'galarm-config'
import Utils from '../utils/Utils'
import { Platform } from 'react-native'
import objGet from 'lodash/get'
import { REHYDRATE } from 'redux-persist'

const INITIAL_STATE = {
  ringParticipantAlarmsByDefault:
    GlobalConfig.defaultValueForRingParticipantAlarmsByDefault,
  ringOnVibrate: GlobalConfig.defaultRingOnVibrate,
  timeFormat: GlobalConfig.defaultTimeFormat,
  additionalSetupInstructions: {},
  removedSystemMessages: [],
  hiddenSystemMessages: [],
  alarmRingtone: GlobalConfig.defaultAlarmRingtone,
  preReminderRingtone: GlobalConfig.defaultPreReminderRingtone,
  timerRingtone: GlobalConfig.defaultTimerRingtone,
  vibrate: GlobalConfig.defaultVibrate,
  ringOnEarphoneOnly: GlobalConfig.defaultRingOnEarphoneOnly,
  gestureToDismissAnAlarm: GlobalConfig.defaultGestureToDismissAlarm,
  graduallyIncreaseVolume: GlobalConfig.defaultGraduallyIncreaseVolume,
  shakeToStopAlarm: GlobalConfig.defaultShakeToStopAlarm,
  shakeSensitivity: GlobalConfig.defaultShakeSensitivity,
  volume: GlobalConfig.defaultVolume,
  ringtoneDuration: GlobalConfig.defaultRingtoneDuration,
  autoSnooze: GlobalConfig.defaultAutoSnooze,
  autoSnoozeDuration: GlobalConfig.defaultAutoSnoozeDuration,
  autoSnoozeCount: GlobalConfig.defaultAutoSnoozeCount,
  skuDetails: {},
  announceAlarmName: GlobalConfig.defaultAnnounceAlarmName,
  colorScheme: GlobalConfig.defaultColorScheme,
  categorizedAlarmView: Platform.OS === 'web' ? false : true,
  webColorScheme: GlobalConfig.defaultColorScheme,
  primaryColorId: GlobalConfig.defaultPrimaryColorId,
  specifyTimezoneForAlarm: GlobalConfig.defaultSpecifyTimezoneForAlarm,
  defaultTimezoneForAlarm: '',
  notificationSettings: GlobalConfig.defaultNotificationSettings,
  muteAlarms: false,
  criticalAlerts: GlobalConfig.defaultCriticalAlerts,
  criticalAlertsVolume: GlobalConfig.defaultCriticalAlertsVolume,
  firstDayOfWeek: GlobalConfig.defaultFirstDayOfWeek,
  notificationActionBehavior: GlobalConfig.defaultNotificationActionBehavior,
  webFirstDayOfWeek: GlobalConfig.defaultFirstDayOfWeek,
  promptForUpdate: true,
  pinMode: GlobalConfig.defaultPinMode,
  sortAlarmCategoriesBy: GlobalConfig.defaultSortAlarmCategoriesBy,
  missedAlarmPlacementSetting: GlobalConfig.defaultMissedAlarmPlacementSetting,
  enhancedNotifications: GlobalConfig.defaultEnhancedNotifications,
  appTerminatedNotification: GlobalConfig.defaultAppTerminatedNotification,
  fabButtonBehavior: {
    mode: 'classic',
    customOptions: {
      quickReminder: true,
      personalAlarm: true,
      groupAlarm: true,
      buddyAlarm: true,
      instantAlert: true
    }
  }
}

const rehydrateState = (state, action) => {
  const persistedSettings = objGet(
    action,
    'payload.userSettings',
    INITIAL_STATE
  )
  return {
    ...state,
    ...persistedSettings
  }
}

const setUserSettings = (state, action) => {
  return {
    ...state,
    ...action.payload.userSettings
  }
}
const setRingParticipantAlarmsByDefault = (state, action) => ({
  ...state,
  ringParticipantAlarmsByDefault: action.payload.ringParticipantAlarmsByDefault
})

const setGlobalAlarmRingerSettings = (state, action) => ({
  ...state,
  ...action.payload.ringerSettings
})

const setRingOnEarphoneOnly = (state, action) => ({
  ...state,
  ringOnEarphoneOnly: action.payload.ringOnEarphoneOnly
})

const setGestureToDismissAnAlarm = (state, action) => ({
  ...state,
  gestureToDismissAnAlarm: action.payload.gestureToDismissAnAlarm
})

const setSpecifyTimezoneForAlarm = (state, action) => ({
  ...state,
  specifyTimezoneForAlarm: action.payload.specifyTimezoneForAlarm
})

const setDefaultTimezoneForAlarm = (state, action) => ({
  ...state,
  defaultTimezoneForAlarm: action.payload.defaultTimezoneForAlarm
})

const setGraduallyIncreaseVolume = (state, action) => ({
  ...state,
  graduallyIncreaseVolume: action.payload.graduallyIncreaseVolume
})

const setShakeToStopAlarm = (state, action) => ({
  ...state,
  shakeToStopAlarm: action.payload.shakeToStopAlarm
})

const setShakeSensitivity = (state, action) => ({
  ...state,
  shakeSensitivity: action.payload.shakeSensitivity
})

const setPromptForUpdate = (state, action) => ({
  ...state,
  promptForUpdate: action.payload.promptForUpdate
})

const setPinMode = (state, action) => ({
  ...state,
  pinMode: action.payload.pinMode
})

const setAlarmRingtone = (state, action) => ({
  ...state,
  alarmRingtone: action.payload.alarmRingtone
})

const setPreReminderRingtone = (state, action) => ({
  ...state,
  preReminderRingtone: action.payload.preReminderRingtone
})

const setTimerRingtone = (state, action) => ({
  ...state,
  timerRingtone: action.payload.timerRingtone
})

const setTimeFormat = (state, action) => ({
  ...state,
  timeFormat: action.payload.timeFormat
})

const setColorScheme = (state, action) => ({
  ...state,
  colorScheme: action.payload.colorScheme
})

const setWebColorScheme = (state, action) => ({
  ...state,
  webColorScheme: action.payload.webColorScheme
})

const setPrimaryColorId = (state, action) => ({
  ...state,
  primaryColorId: action.payload.primaryColorId
})

const setAdditionalSetupInstructions = (state, action) => ({
  ...state,
  additionalSetupInstructions: action.payload.additionalSetupInstructions
})

const setNotificationSettings = (state, action) => ({
  ...state,
  notificationSettings: action.payload.notificationSettings
})

const addHiddenSystemMessage = (state, action) => {
  const systemMessageId = action.payload.id

  const systemMessageIndex = Utils.getIndexOfObjectWithId(
    state.hiddenSystemMessages,
    systemMessageId
  )

  let newHiddenSystemMessages

  // New system message
  if (systemMessageIndex === -1) {
    newHiddenSystemMessages = [
      ...state.hiddenSystemMessages,
      {
        id: systemMessageId,
        remindMeLaterTime: Date.now()
      }
    ]
  } else {
    // Update the system message time
    newHiddenSystemMessages = state.hiddenSystemMessages.map((msg, index) =>
      index === systemMessageIndex
        ? {
            id: systemMessageId,
            remindMeLaterTime: Date.now()
          }
        : msg
    )
  }
  return {
    ...state,
    hiddenSystemMessages: newHiddenSystemMessages
  }
}
const removeSystemMessage = (state, action) => {
  const systemMessageId = action.payload.id

  const newRemovedSystemMessages = [
    ...state.removedSystemMessages,
    systemMessageId
  ]

  return {
    ...state,
    removedSystemMessages: newRemovedSystemMessages
  }
}

const addSkuDetails = (state, action) => {
  const skuDetails = action.payload.skuDetails
  if (!skuDetails || !Array.isArray(skuDetails) || skuDetails.length === 0) {
    return state
  }
  const newSkuDetails = { ...state.skuDetails }
  skuDetails.forEach(skuDetail => {
    newSkuDetails[skuDetail.id] = skuDetail
  })
  return {
    ...state,
    skuDetails: newSkuDetails
  }
}

const toggleCategorizedAlarmView = state => {
  return {
    ...state,
    categorizedAlarmView: !state.categorizedAlarmView
  }
}

const setMuteAlarms = (state, action) => {
  return {
    ...state,
    muteAlarms: action.payload.muteAlarms
  }
}

const setFirstDayOfWeek = (state, action) => {
  return {
    ...state,
    firstDayOfWeek: action.payload.firstDayOfWeek
  }
}

const setWebFirstDayOfWeek = (state, action) => {
  return {
    ...state,
    webFirstDayOfWeek: action.payload.webFirstDayOfWeek
  }
}

const setNotificationActionBehavior = (state, action) => {
  return {
    ...state,
    notificationActionBehavior: action.payload.notificationActionBehavior
  }
}

const setSortAlarmCategoriesBy = (state, action) => {
  return {
    ...state,
    sortAlarmCategoriesBy: action.payload.sortAlarmCategoriesBy
  }
}

const setMissedAlarmPlacementSetting = (state, action) => {
  const missedAlarmPlacementSetting = action.payload.missedAlarmPlacementSetting
  return {
    ...state,
    missedAlarmPlacementSetting
  }
}

const setEnhancedNotifications = (state, action) => {
  return {
    ...state,
    enhancedNotifications: action.payload.enhancedNotifications
  }
}

const setAppTerminatedNotification = (state, action) => {
  return {
    ...state,
    appTerminatedNotification: action.payload.appTerminatedNotification
  }
}

const setFabButtonBehavior = (state, action) => ({
  ...state,
  fabButtonBehavior: action.payload.fabButtonBehavior
})

const ACTION_HANDLERS = {
  [REHYDRATE]: rehydrateState,
  [ActionTypes.SET_USER_SETTINGS]: setUserSettings,
  [ActionTypes.SET_RING_PARTICIPANT_ALARMS_BY_DEFAULT]:
    setRingParticipantAlarmsByDefault,
  [ActionTypes.SET_ALARM_RINGTONE]: setAlarmRingtone,
  [ActionTypes.SET_PRE_REMINDER_RINGTONE]: setPreReminderRingtone,
  [ActionTypes.SET_TIMER_RINGTONE]: setTimerRingtone,
  [ActionTypes.SET_TIME_FORMAT]: setTimeFormat,
  [ActionTypes.SET_COLOR_SCHEME]: setColorScheme,
  [ActionTypes.SET_WEB_COLOR_SCHEME]: setWebColorScheme,
  [ActionTypes.SET_PRIMARY_COLOR_ID]: setPrimaryColorId,
  [ActionTypes.SET_ADDITIONAL_SETUP_INSTRUCTIONS]:
    setAdditionalSetupInstructions,
  [ActionTypes.REMOVE_SYSTEM_MESSAGE]: removeSystemMessage,
  [ActionTypes.ADD_HIDDEN_SYSTEM_MESSAGE]: addHiddenSystemMessage,
  [ActionTypes.SET_GLOBAL_ALARM_RINGER_SETTINGS]: setGlobalAlarmRingerSettings,
  [ActionTypes.ADD_SKU_DETAILS]: addSkuDetails,
  [ActionTypes.TOGGLE_CATEGORIZED_ALARM_VIEW]: toggleCategorizedAlarmView,
  [ActionTypes.SET_RING_ON_EARPHONE_ONLY]: setRingOnEarphoneOnly,
  [ActionTypes.SET_GESTURE_TO_DISMISS_ALARM]: setGestureToDismissAnAlarm,
  [ActionTypes.SET_SPECIFY_TIMEZONE_FOR_ALARM]: setSpecifyTimezoneForAlarm,
  [ActionTypes.SET_DEFAULT_TIMEZONE_FOR_ALARM]: setDefaultTimezoneForAlarm,
  [ActionTypes.SET_GRADUALLY_INCREASE_VOLUME]: setGraduallyIncreaseVolume,
  [ActionTypes.SET_SHAKE_TO_STOP_ALARM]: setShakeToStopAlarm,
  [ActionTypes.SET_SHAKE_SENSITIVITY]: setShakeSensitivity,
  [ActionTypes.SET_NOTIFICATION_SETTINGS]: setNotificationSettings,
  [ActionTypes.SET_MUTE_ALARMS]: setMuteAlarms,
  [ActionTypes.SET_FIRST_DAY_OF_WEEK]: setFirstDayOfWeek,
  [ActionTypes.SET_WEB_FIRST_DAY_OF_WEEK]: setWebFirstDayOfWeek,
  [ActionTypes.SET_NOTIFICATION_ACTION_BEHAVIOR]: setNotificationActionBehavior,
  [ActionTypes.SET_PIN_MODE]: setPinMode,
  [ActionTypes.SET_PROMPT_FOR_UPDATE]: setPromptForUpdate,
  [ActionTypes.SET_SORT_ALARM_CATEGORIES_BY]: setSortAlarmCategoriesBy,
  [ActionTypes.SET_MISSED_ALARM_PLACEMENT_SETTING]:
    setMissedAlarmPlacementSetting,
  [ActionTypes.SET_ENHANCED_NOTIFICATIONS]: setEnhancedNotifications,
  [ActionTypes.SET_APP_TERMINATED_NOTIFICATION]: setAppTerminatedNotification,
  [ActionTypes.SET_FAB_BUTTON_BEHAVIOR]: setFabButtonBehavior
}

export default createReducer(INITIAL_STATE, ACTION_HANDLERS)
